import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TrendUp, ArrowLeft, PencilSimple, Trash, CheckSquare, Square, Warning, CurrencyInr, Receipt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { EditSaleDialog } from '@/components/sales/EditSaleDialog';

const API = `${window.location.origin}/api`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'bg-green-100 text-green-800 border-green-300',
    partial: 'bg-amber-100 text-amber-800 border-amber-300',
    pending: 'bg-red-100 text-red-800 border-red-300',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {(status || 'pending').charAt(0).toUpperCase() + (status || 'pending').slice(1)}
    </span>
  );
};

export const SalesDetailView = ({ onBack }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingSale, setEditingSale] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/sales/detailed`);
      setSales(response.data);
    } catch (err) {
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  // Computed stats
  const totalBilled = sales.reduce((sum, s) => sum + (s.total_bill_amount || s.amount || 0), 0);
  const totalReceived = sales.reduce((sum, s) => sum + (s.amount_paid || 0), 0);
  const totalOutstanding = sales.reduce((sum, s) => sum + (s.balance_amount || 0), 0);
  const pendingSales = sales.filter(s => s.payment_status === 'pending' || s.payment_status === 'partial');

  const getChartData = () => {
    const salesByDate = sales.reduce((acc, sale) => {
      acc[sale.date] = (acc[sale.date] || 0) + (sale.total_bill_amount || sale.amount || 0);
      return acc;
    }, {});
    return Object.entries(salesByDate).sort(([a], [b]) => a.localeCompare(b)).slice(-7).map(([date, amount]) => ({ date, amount }));
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  const filteredSales = sales
    .filter(sale => {
      if (showPendingOnly && sale.payment_status !== 'pending' && sale.payment_status !== 'partial') return false;
      const q = searchQuery.toLowerCase();
      return (sale.customer_name || '').toLowerCase().includes(q) ||
        (sale.bill_number || '').toLowerCase().includes(q);
    });

  const handleDelete = async (sale) => {
    if (!window.confirm(`Delete sale ${sale.bill_number || sale.sale_id}?`)) return;
    try {
      if (sale.sale_id) {
        await axios.delete(`${API}/sales/${sale.sale_id}`);
      } else {
        await axios.delete(`${API}/sales`, { params: { date: sale.date, bill_number: sale.bill_number } });
      }
      toast.success('Sale deleted!');
      setSelectedIds(prev => prev.filter(id => id !== sale.sale_id));
      fetchSales();
    } catch (err) {
      toast.error('Failed to delete sale');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected sales?`)) return;
    try {
      await axios.post(`${API}/sales/bulk-delete`, { sale_ids: selectedIds });
      toast.success(`${selectedIds.length} sales deleted!`);
      setSelectedIds([]);
      fetchSales();
    } catch (err) {
      toast.error('Failed to delete sales');
    }
  };

  const toggleSelect = (saleId) => {
    if (!saleId) return;
    setSelectedIds(prev => prev.includes(saleId) ? prev.filter(id => id !== saleId) : [...prev, saleId]);
  };

  const toggleSelectAll = () => {
    const selectableIds = filteredSales.filter(s => s.sale_id).map(s => s.sale_id);
    setSelectedIds(prev => prev.length === selectableIds.length ? [] : selectableIds);
  };

  const handleSaveEdit = async (updatedForm) => {
    try {
      if (editingSale.sale_id) {
        await axios.put(`${API}/sales/${editingSale.sale_id}`, updatedForm);
      } else {
        await axios.put(`${API}/sales`, updatedForm, {
          params: { date: editingSale.date, bill_number: editingSale.bill_number }
        });
      }
      toast.success('Sale updated!');
      setEditingSale(null);
      fetchSales();
    } catch (err) {
      toast.error('Failed to update sale');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1] p-6" data-testid="sales-detail-view">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="premium-button" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" /> Back
          </Button>
          <h1 className="heading text-4xl font-bold gradient-text">Sales Details</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="premium-card p-5" data-testid="stat-total-billed">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Billed</p>
                <h3 className="currency text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totalBilled)}</h3>
              </div>
              <Receipt size={32} className="text-indigo-400" weight="bold" />
            </div>
          </Card>
          <Card className="premium-card p-5" data-testid="stat-amount-received">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount Received</p>
                <h3 className="currency text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalReceived)}</h3>
              </div>
              <CurrencyInr size={32} className="text-green-400" weight="bold" />
            </div>
          </Card>
          <Card className="premium-card p-5" data-testid="stat-outstanding">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Outstanding</p>
                <h3 className="currency text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalOutstanding)}</h3>
              </div>
              <Warning size={32} className="text-red-400" weight="bold" />
            </div>
          </Card>
          <Card className="premium-card p-5" data-testid="stat-transactions">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Transactions</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{sales.length}</h3>
                <p className="text-xs text-amber-600 mt-1">{pendingSales.length} pending/partial</p>
              </div>
              <TrendUp size={32} className="text-indigo-400" weight="bold" />
            </div>
          </Card>
        </div>

        {/* Chart */}
        <Card className="premium-card p-6 mb-6">
          <h2 className="heading text-lg font-bold mb-4 text-gray-900">Sales Trend</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={getChartData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e0" />
              <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 12 }} />
              <YAxis tick={{ fill: '#666', fontSize: 12 }} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Pending Bills Tracker */}
        {pendingSales.length > 0 && (
          <Card className="premium-card p-4 mb-6 border-l-4 border-l-amber-500 bg-amber-50/50" data-testid="pending-bills-tracker">
            <div className="flex items-center justify-between mb-3">
              <h2 className="heading text-base font-bold text-amber-800 flex items-center gap-2">
                <Warning size={20} weight="bold" />
                Pending Bills Tracker ({pendingSales.length})
              </h2>
              <Button
                size="sm"
                variant={showPendingOnly ? "default" : "outline"}
                onClick={() => setShowPendingOnly(!showPendingOnly)}
                className={showPendingOnly ? "bg-amber-600 hover:bg-amber-700 text-white text-xs" : "text-xs"}
                data-testid="toggle-pending-filter"
              >
                {showPendingOnly ? 'Show All' : 'Filter Pending Only'}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {pendingSales.slice(0, 6).map((sale, idx) => (
                <div key={sale.sale_id || idx} className="flex items-center justify-between bg-white rounded-lg p-2.5 border border-amber-200">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{sale.bill_number || 'No Bill#'}</p>
                    <p className="text-xs text-gray-500 truncate">{sale.customer_name || 'Walk-in'}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="font-bold text-sm text-red-600">{formatCurrency(sale.balance_amount)}</p>
                    <StatusBadge status={sale.payment_status} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Search + Bulk Actions */}
        <Card className="premium-card p-4 mb-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Search by customer name, bill number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 focus:ring-2 focus:ring-purple-500 flex-1"
              data-testid="sales-search"
            />
            {selectedIds.length > 0 && (
              <Button
                size="sm"
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap"
                data-testid="bulk-delete-btn"
              >
                <Trash size={16} className="mr-1" />
                Delete ({selectedIds.length})
              </Button>
            )}
          </div>
        </Card>

        {/* Sales Table */}
        <Card className="premium-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="premium-table w-full" data-testid="sales-table">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="px-3 py-3 text-center w-10">
                    <button onClick={toggleSelectAll} className="text-gray-500 hover:text-indigo-600" data-testid="select-all-checkbox">
                      {selectedIds.length > 0 && selectedIds.length === filteredSales.filter(s => s.sale_id).length
                        ? <CheckSquare size={18} weight="bold" className="text-indigo-600" />
                        : <Square size={18} />}
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bill No</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Bill</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Paid</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Method</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSales.length === 0 ? (
                  <tr><td colSpan={10} className="px-6 py-12 text-center text-gray-500">No sales found</td></tr>
                ) : filteredSales.map((sale, index) => {
                  const totalBill = sale.total_bill_amount || sale.amount || 0;
                  const amountPaid = sale.amount_paid || 0;
                  const balance = sale.balance_amount || 0;
                  return (
                    <tr key={sale.sale_id || `row-${index}`} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(sale.sale_id) ? 'bg-indigo-50' : ''}`} data-testid={`sale-row-${index}`}>
                      <td className="px-3 py-3 text-center">
                        <button onClick={() => toggleSelect(sale.sale_id)} className="text-gray-400 hover:text-indigo-600" data-testid={`select-sale-${index}`}>
                          {selectedIds.includes(sale.sale_id) ? <CheckSquare size={16} weight="bold" className="text-indigo-600" /> : <Square size={16} />}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-bold text-gray-900 text-sm">{sale.bill_number || '-'}</span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="customer-avatar w-7 h-7 text-xs">{getInitials(sale.customer_name || 'Guest')}</div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{sale.customer_name || 'Walk-in'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3"><span className="date-text text-sm text-gray-600">{sale.date}</span></td>
                      <td className="px-3 py-3 text-right">
                        <span className="currency font-bold text-gray-900 text-sm">{formatCurrency(totalBill)}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="currency font-semibold text-green-700 text-sm">{formatCurrency(amountPaid)}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className={`currency font-semibold text-sm ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(balance)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <StatusBadge status={sale.payment_status} />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs font-medium text-gray-600 capitalize">{sale.payment_method || 'cash'}</span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditingSale(sale)} className="h-7 w-7 p-0 hover:bg-indigo-50" data-testid={`edit-sale-${index}`}>
                            <PencilSimple size={14} className="text-indigo-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(sale)} className="h-7 w-7 p-0 hover:bg-red-50" data-testid={`delete-sale-${index}`}>
                            <Trash size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {editingSale && (
        <EditSaleDialog
          sale={editingSale}
          onSave={handleSaveEdit}
          onClose={() => setEditingSale(null)}
        />
      )}
    </div>
  );
};

export default SalesDetailView;
